import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LogIn, LogOut, Coffee, PlayCircle } from "lucide-react";

function computeElapsedMs(record, nowMs) {
  const checkIn = new Date(record.check_in_time).getTime();
  const breaks = record.breaks || [];
  let breakMs = 0;
  let referenceEnd = nowMs;
  for (const b of breaks) {
    if (b.end) breakMs += new Date(b.end).getTime() - new Date(b.start).getTime();
    else referenceEnd = new Date(b.start).getTime();
  }
  return Math.max(0, referenceEnd - checkIn - breakMs);
}

function formatDuration(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function AttendanceWidget({ record, onChange }) {
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const isOpen = !!record && !record.check_out_time;
  const onBreak = record?.status === "On Break";

  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  const call = async (endpoint, successMsg) => {
    setBusy(true);
    try {
      const res = await api.post(`/attendance/${endpoint}`);
      toast.success(successMsg);
      onChange(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex flex-wrap items-center justify-between gap-4" data-testid="attendance-widget">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-[#4B5563] font-semibold mb-1">Attendance</p>
        {isOpen ? (
          <>
            <p className="font-heading text-3xl font-bold text-[#1C1C1E]" data-testid="attendance-live-timer">
              {formatDuration(computeElapsedMs(record, now))}
            </p>
            <p className="text-xs text-[#4B5563] mt-1">{onBreak ? "On break — timer paused" : "Net working time today"}</p>
          </>
        ) : (
          <p className="text-sm text-[#4B5563]" data-testid="attendance-not-checked-in">Not checked in today.</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!isOpen && (
          <Button data-testid="checkin-button" onClick={() => call("check-in", "Checked in — have a productive day!")} disabled={busy} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            <LogIn className="h-4 w-4 mr-1.5" /> I'm In
          </Button>
        )}
        {isOpen && !onBreak && (
          <>
            <Button data-testid="start-break-button" variant="outline" onClick={() => call("break/start", "Break started")} disabled={busy}>
              <Coffee className="h-4 w-4 mr-1.5" /> Start Break
            </Button>
            <Button data-testid="checkout-button" onClick={() => call("check-out", "Checked out — see you next time!")} disabled={busy} className="bg-[#00823B] hover:bg-[#006A2F] text-white">
              <LogOut className="h-4 w-4 mr-1.5" /> I'm Out
            </Button>
          </>
        )}
        {isOpen && onBreak && (
          <Button data-testid="end-break-button" onClick={() => call("break/end", "Break ended, back to work")} disabled={busy} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            <PlayCircle className="h-4 w-4 mr-1.5" /> End Break
          </Button>
        )}
      </div>
    </div>
  );
}
